/**
 * Icon — Web implementation.
 *
 * Renders inline SVGs using shared path data from Icon.ts.
 */

import React from 'react';
import type { IconName } from '../../elements/icons';
import { DEFAULT_ICON_SIZE, iconColorFor, iconDefs, type IconTone } from './Icon';

export interface WebIconProps {
  name: IconName;
  size?: number;
  color?: string;
  /** Semantic tint from the `colors.icon` token group; wins over `color`. */
  tone?: IconTone;
  'aria-label'?: string;
  'aria-hidden'?: boolean;
}

export function Icon({
  name,
  size = DEFAULT_ICON_SIZE,
  color,
  tone,
  'aria-label': ariaLabel,
  'aria-hidden': ariaHidden,
}: WebIconProps) {
  const def = iconDefs[name];
  const resolvedColor = iconColorFor({ color, tone });

  return (
    <svg
      width={size}
      height={size}
      viewBox={def.viewBox}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role={ariaLabel ? 'img' : undefined}
      aria-label={ariaLabel}
      aria-hidden={ariaHidden ?? !ariaLabel}
    >
      {def.paths.map((p, i) => (
        <path
          key={i}
          d={p.d}
          fill={p.fill === 'none' ? 'none' : (p.fill ?? resolvedColor)}
          stroke={p.stroke === 'currentColor' ? resolvedColor : p.stroke}
          strokeWidth={p.strokeWidth}
          strokeLinecap={p.strokeLinecap}
          strokeLinejoin={p.strokeLinejoin}
          fillRule={p.fillRule}
          clipRule={p.clipRule}
        />
      ))}
    </svg>
  );
}
